<template>
  <b-container
    class="parameters-tab"
    fluid
  >
    <b-row>
      <b-col
        v-show="loading"
        class="text-center my-5"
        cols="12"
      >
        <s-loader
          size="lg"
        />
      </b-col>
      <accordion-panel
        v-show="!loading"
        class="mb-3"
        id="parameters"
        :collapsed.sync="collapsed"
      >
        <template #title>
          <strong> {{ $t('monitoring.alarm') }} </strong>
        </template>
        <b-form-group>
          <label>
            {{
              $t('fields.engineering.commutator-maintenance.days-advance-maintenance-notice')
            }}*
          </label>
          <b-row class="m-0">
            <b-col
              cols="12"
              md="4"
              xl="3"
            >
              <s-input-label-edit
                v-model="daysAdvanceMaintenanceNotice"
                :editable="editMode"
              >
                <s-number-input
                  v-model="daysAdvanceMaintenanceNotice"
                  :validation="$v['daysAdvanceMaintenanceNotice']"
                  :numeric-options="defaultNumericOptions"
                  :invalid="$v['daysAdvanceMaintenanceNotice'].$invalid"
                  @input="$v.daysAdvanceMaintenanceNotice.$touch()"
                />
                <b-form-invalid-feedback
                  v-if="$v.daysAdvanceMaintenanceNotice.$error"
                  class="input-live-feedback"
                  id="input-live-feedback"
                >
                  {{ getRangeMessage(1, 1200) }}
                </b-form-invalid-feedback>
              </s-input-label-edit>
            </b-col>
          </b-row>
        </b-form-group>
        <b-form-group>
          <label>
            {{
              $t('fields.engineering.commutator-maintenance.remaining-commutations')
            }}*
          </label>
          <b-row class="m-0">
            <b-col
              cols="12"
              md="4"
              xl="3"
            >
              <s-input-label-edit
                v-model="remainingCommutationsWarningMaintenance"
                :editable="editMode"
              >
                <s-number-input
                  v-model="remainingCommutationsWarningMaintenance"
                  :validation="$v['remainingCommutationsWarningMaintenance']"
                  :numeric-options="defaultNumericOptions"
                  :invalid="$v['remainingCommutationsWarningMaintenance'].$invalid"
                  @input="$v.remainingCommutationsWarningMaintenance.$touch()"
                />
                <b-form-invalid-feedback
                  v-if="$v.remainingCommutationsWarningMaintenance.$error"
                  class="input-live-feedback"
                  id="input-live-feedback"
                >
                  {{ getRangeMessage(1, 110000) }}
                </b-form-invalid-feedback>
              </s-input-label-edit>
            </b-col>
          </b-row>
        </b-form-group>
      </accordion-panel>
    </b-row>
    <b-row
      v-show="$hasUserPermission('PER013')"
      class="no-internal-print"
      align-h="end"
    >
      <b-col
        v-if="editMode"
        class="mt-3 mr-sm-2 p-0"
        cols="12"
        sm="4"
        md="3"
        xl="2"
      >
        <b-button
          class="parameters-tab__btn"
          block
          variant="secondary"
          @click="callCancel"
        >
          {{ $t('actions.cancel') }}
        </b-button>
      </b-col>
      <b-col
        v-if="!editMode"
        class="mt-3 p-0"
        cols="12"
        sm="4"
        md="3"
        xl="2"
      >
        <b-button
          v-show="!loading"
          class="parameters-tab__btn"
          block
          variant="primary"
          @click="handleEdit(true)"
        >
          {{ $t('actions.edit') }}
        </b-button>
      </b-col>
      <b-col
        v-else
        class="mt-3 p-0"
        cols="12"
        sm="4"
        md="3"
        xl="2"
      >
        <b-button
          block
          variant="primary"
          :disabled="!canSave"
          @click="confirmSave"
        >
          {{ $t('actions.save') }}
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import AccordionPanel from '@/components/AccordionPanel';
import globalEventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import Service from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import { required, maxValue, minValue } from 'vuelidate/lib/validators';

export default {
  name: 'CommutatorMaintenanceParameters',
  components: {
    AccordionPanel,
  },
  data: () => ({
    collapsed: false,
    editMode: false,
    loading: false,
    daysAdvanceMaintenanceNotice: null,
    remainingCommutationsWarningMaintenance: null,
    backupValues: {},
  }),
  validations: {
    daysAdvanceMaintenanceNotice: {
      required,
      minValue: minValue(1),
      maxValue: maxValue(1200),
    },
    remainingCommutationsWarningMaintenance: {
      required,
      minValue: minValue(1),
      maxValue: maxValue(110000),
    },
  },
  computed: {
    canSave() {
      return !this.$v.$invalid;
    },
    defaultNumericOptions() {
      return {
        decimalPlaces: 0,
      };
    },
  },
  created() {
    this.getParameters(true);
    this.initListener();
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    getRangeMessage(minimumValue, maximumValue) {
      return this.$t('messages.MA086.message', {
        minValue: this.$n(minimumValue),
        maxValue: this.$n(maximumValue),
      });
    },
    initListener() {
      globalEventBus.on(events.refreshDataTab, this.refreshDataEvent, this);
    },
    removeListener() {
      globalEventBus.off(events.refreshDataTab, this.refreshDataEvent, this);
    },
    refreshDataEvent({ wait }) {
      wait(this.getParameters());
    },
    getParameters(showLoading = false) {
      this.loading = showLoading;
      const { assetModuleId } = this.$route.params;
      return Service.getParameters(assetModuleId).then((data) => {
        this.daysAdvanceMaintenanceNotice = data.daysAdvanceMaintenanceNotice;
        this.remainingCommutationsWarningMaintenance = data.remainingCommutationsWarningMaintenance;
        this.backupValues = data;
      }).finally(() => {
        this.loading = false;
      });
    },
    confirmSave() {
      this.$confirmMessage({ code: 'MC048' }).accepted(() => {
        this.save();
      });
    },
    save() {
      const { assetModuleId } = this.$route.params;
      const { daysAdvanceMaintenanceNotice, remainingCommutationsWarningMaintenance } = this;
      const parameter = {
        daysAdvanceMaintenanceNotice,
        remainingCommutationsWarningMaintenance,
      };
      return Service.setParameters(assetModuleId, parameter).then(() => {
        this.$pushAlert({ code: 'MS032' });
        this.handleEdit(false);
      });
    },
    handleEdit(value) {
      this.editMode = value;
    },
    callCancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.cancelEdit();
      });
    },
    cancelEdit() {
      const {
        daysAdvanceMaintenanceNotice,
        remainingCommutationsWarningMaintenance,
      } = this.backupValues;
      this.daysAdvanceMaintenanceNotice = daysAdvanceMaintenanceNotice;
      this.remainingCommutationsWarningMaintenance = remainingCommutationsWarningMaintenance;
      this.editMode = false;
    },
  },
};
</script>
<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .parameters-tab
    ::v-deep
      .accordion-panel
        padding: 5px
        border: 1px solid $color-border-gray
        border-radius: 3px
        &__header
          height: 30px
          padding: 0 15px !important
          display: flex
          align-items: center
        > div
          margin: 0
      .input-live-feedback
        min-width: 40vw
</style>
